import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { Box, Button, Typography } from "@mui/material";

import Images from "../utils/Images";

const CARD_GAP = 10;
const CARD_PEEK = 18;
const CARD_ASPECT_RATIO = "163 / 76";
const CARD_WIDTH_TWO = `calc((100cqi - ${CARD_GAP * 2}px - ${CARD_PEEK}px) / 2)`;
const CARD_WIDTH_THREE = `calc((100cqi - ${CARD_GAP * 3}px - ${CARD_PEEK}px) / 3)`;
const MARQUEE_DURATION_MS = 14000;

const AdvertisementCarousel = () => {
  const viewportRef = useRef(null);
  const appDownloadUrl = "https://r8rpc-alternate.app.link";

  const advertisements = [
    {
      id: 1,
      image: Images.banner1,
      alt: "Classic Cold Coffee at 79",
    },
    {
      id: 2,
      image: Images.banner2,
      alt: "First drink at 69",
    },
    {
      id: 3,
      image: Images.banner3,
      alt: "Refer a friend and get drink at 69",
    },
  ];

  const [cardWidth, setCardWidth] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const loopSets = [0, 1];

  useLayoutEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return;
    }

    const updateCardWidth = () => {
      const styles = window.getComputedStyle(viewport);
      const paddingLeft = parseFloat(styles.paddingLeft) || 0;
      const paddingRight = parseFloat(styles.paddingRight) || 0;
      const available = viewport.clientWidth - paddingLeft - paddingRight;

      if (available <= 0) {
        return;
      }

      const visibleCards = available >= 640 ? 3 : 2;
      const nextWidth =
        (available - CARD_GAP * visibleCards - CARD_PEEK) / visibleCards;

      setCardWidth(Math.max(120, nextWidth));
    };

    updateCardWidth();
    const rafId = window.requestAnimationFrame(updateCardWidth);

    const resizeObserver = new ResizeObserver(updateCardWidth);
    resizeObserver.observe(viewport);

    return () => {
      window.cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;

    if (!viewport) {
      return;
    }

    const pause = () => setIsPaused(true);
    const resume = () => setIsPaused(false);

    viewport.addEventListener("pointerdown", pause);
    window.addEventListener("pointerup", resume);
    window.addEventListener("pointercancel", resume);

    return () => {
      viewport.removeEventListener("pointerdown", pause);
      window.removeEventListener("pointerup", resume);
      window.removeEventListener("pointercancel", resume);
    };
  }, []);

  const renderBannerSet = (setIndex, hidden = false) => (
    <Box
      key={`ad-set-${setIndex}`}
      aria-hidden={hidden ? "true" : undefined}
      sx={{
        display: "flex",
        flexShrink: 0,
        alignItems: "flex-start",
        gap: `${CARD_GAP}px`,
        pr: `${CARD_GAP}px`,
      }}
    >
      {advertisements.map((advertisement) => (
        <Box
          key={`${setIndex}-${advertisement.id}`}
          sx={{
            position: "relative",
            flex: "0 0 auto",
            alignSelf: "flex-start",
            width: cardWidth ? `${cardWidth}px` : CARD_WIDTH_TWO,
            height: cardWidth ? `${(cardWidth * 76) / 163}px` : "auto",
            aspectRatio: CARD_ASPECT_RATIO,
            ...(cardWidth
              ? {}
              : {
                  "@container (min-width: 640px)": {
                    width: CARD_WIDTH_THREE,
                  },
                }),
            borderRadius: {
              xs: "8px",
              sm: "9px",
            },
            overflow: "hidden",
            backgroundColor: "transparent",
            lineHeight: 0,
          }}
        >
          <Box
            component="img"
            src={advertisement.image}
            alt={hidden ? "" : advertisement.alt}
            draggable={false}
            sx={{
              position: "absolute",
              inset: 0,
              display: "block",
              width: "100%",
              height: "100%",
              maxWidth: "none",
              maxHeight: "none",
              objectFit: "cover",
              objectPosition: "center",
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        boxSizing: "border-box",
        px: {
          xs: "10px",
          sm: "16px",
        },
        py: {
          xs: "10px",
          sm: "12px",
        },
      }}
    >
      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          overflow: "hidden",
          borderRadius: {
            xs: "16px",
            sm: "18px",
          },
          background:
            "linear-gradient(180deg, #EDFF43 0%, #F8FF94 45%, #FBFFD1 100%)",
          pt: {
            xs: "12px",
            sm: "14px",
          },
          pb: {
            xs: "14px",
            sm: "16px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: {
              xs: "8px",
              sm: "12px",
            },
            px: "16px",
            mb: {
              xs: "12px",
              sm: "14px",
            },
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: {
                xs: "18px",
                sm: "21px",
              },
              lineHeight: 1,
              transform: "rotate(-10deg)",
            }}
          >
            🎉
          </Typography>

          <Typography
            sx={{
              color: "#063E69",
              fontWeight: 600,
              fontSize: {
                xs: "17px",
                sm: "20px",
              },
              lineHeight: 1.1,
              letterSpacing: "-0.4px",
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
          >
            App Exclusive Deals
          </Typography>

          <Typography
            component="span"
            sx={{
              fontSize: {
                xs: "18px",
                sm: "21px",
              },
              lineHeight: 1,
              transform: "rotate(-10deg)",
            }}
          >
            🎉
          </Typography>
        </Box>

        <Box
          ref={viewportRef}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          sx={{
            width: "100%",
            overflow: "hidden",
            containerType: "inline-size",
            pl: {
              xs: "12px",
              sm: "14px",
            },
            pr: 0,
            boxSizing: "border-box",
            touchAction: "pan-y",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              width: "max-content",
              willChange: "transform",
              animation: cardWidth
                ? `xpanseAdMarquee ${MARQUEE_DURATION_MS}ms linear infinite`
                : "none",
              animationPlayState: isPaused ? "paused" : "running",
              "@keyframes xpanseAdMarquee": {
                "0%": {
                  transform: "translate3d(0, 0, 0)",
                },
                "100%": {
                  transform: "translate3d(-50%, 0, 0)",
                },
              },
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
          >
            {loopSets.map((setIndex) =>
              renderBannerSet(setIndex, setIndex === 1)
            )}
          </Box>
        </Box>

        <Box
          sx={{
            mx: {
              xs: "12px",
              sm: "14px",
            },
            mt: {
              xs: "14px",
              sm: "16px",
            },
            px: {
              xs: "16px",
              sm: "20px",
            },
            py: {
              xs: "14px",
              sm: "16px",
            },
            minHeight: {
              xs: "88px",
              sm: "104px",
            },
            borderRadius: {
              xs: "15px",
              sm: "18px",
            },
            backgroundColor: "#FFFFFF",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: {
              xs: "10px",
              sm: "18px",
            },
            boxSizing: "border-box",
          }}
        >
          <Typography
            sx={{
              flex: 1,
              color: "#129CE9",
              fontWeight: 600,
              fontSize: {
                xs: "16px",
                sm: "19px",
                md: "21px",
              },
              lineHeight: {
                xs: 1.12,
                sm: 1.15,
              },
              letterSpacing: "-0.5px",
              textAlign: "left",
            }}
          >
            Download Xpanse APP
            <br />
            to unlock these deals.
          </Typography>

          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              if (!appDownloadUrl) {
                return;
              }

              window.open(appDownloadUrl, "_blank", "noopener,noreferrer");
            }}
            sx={{
              flexShrink: 0,
              minWidth: {
                xs: "125px",
                sm: "160px",
              },
              height: {
                xs: "48px",
                sm: "58px",
              },
              px: {
                xs: "18px",
                sm: "24px",
              },
              borderRadius: "999px",
              backgroundColor: "#139FE9",
              color: "#FFFFFF",
              fontWeight: 800,
              fontSize: {
                xs: "11px",
                sm: "13px",
              },
              lineHeight: 1,
              letterSpacing: "0.3px",
              whiteSpace: "nowrap",
              boxShadow: "none",
              fontFamily: "Roboto Condensed, sans-serif",
              "&:hover": {
                backgroundColor: "#0C91D9",
                boxShadow: "none",
              },
            }}
          >
            DOWNLOAD APP
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AdvertisementCarousel;
